/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  SelectTaskListMultipleCharge,
  TasksApi,
  UpdateTaskDataMultipleCharge,
} from "client";
import { Const } from "const";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useCallApi } from "hooks/CallApiHook";
import { useWaitTaskStatus } from "hooks/WaitTaskStatusHook";
import { useCallback, useState } from "react";
import { useRegisteredMultipleChargeTasks } from "./GetMultipleChargeTasksHook";
import { useApproveTask } from "hooks/ApproveTaskHook";
import { transformSelectTaskJobInfosToInputJobInfos } from "../utils";

export interface RevertRequest {
  origin: SelectTaskListMultipleCharge;
  update: UpdateTaskDataMultipleCharge;
}

export const useRevertMultipleChargeTasks = () => {
  const [request, setRevertRequest] = useState<RevertRequest>();
  const [api, canceler] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.updateTask
  );
  const [getTaskApi, cancelGetTaskApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTaskList
  );

  const { getMultipleChargeTasks, canceler: getMultipleChargeTasksCanceler } =
    useRegisteredMultipleChargeTasks();
  const { requestApprove, cancelApprove } = useApproveTask();
  const waitTaskStatus = useWaitTaskStatus();

  const cancelCallback = useCallback(() => {
    getMultipleChargeTasksCanceler();
    cancelApprove();
    canceler();
    cancelGetTaskApi();
  }, [
    cancelApprove,
    cancelGetTaskApi,
    canceler,
    getMultipleChargeTasksCanceler,
  ]);

  /**
   *  Re-update request to a previous state
   *
   * @param {RevertRequest} request
   * @return {UpdateTaskDataMultipleCharge} request.update
   */
  const createUndoRequest = async (request: RevertRequest) => {
    // Get current jobset (for JobInfos and versionId)
    const currentJobset = await getTaskApi.getTaskListMultipleCharge({
      jobSetId: request.update.jobSetId,
      mapId: request.update.mapId ?? "",
    });

    const currentVersionId = currentJobset.selectTaskInfos?.[0].versionId ?? "";
    const currentJobInfos = currentJobset.selectTaskInfos?.[0].jobInfos ?? [];

    // Use in place of request.update
    const tempUpdate: UpdateTaskDataMultipleCharge = {
      jobSetId: request.origin.jobSetId,
      jobSetName: request.origin.jobSetName,
      mapId: request.update.mapId,
      mapVer: request.update.mapVer,
      fromNodeId: request.origin.startNodeId,
      toNodeId: request.origin.endNodeId,
      frameTypeAndNumbers: request.origin.frameTypeAndNumbers,
      addJobInfos: [],
      updateJobInfos: [],
      deleteJobInfos: [],
      untreatedJobInfos: request.update.untreatedJobInfos,
      startDatetime: request.origin.jobsetStartDatetime,
      endDatetime: request.origin.jobsetEndDatetime,
      resetJobRequestedEndDatetime: request.origin.resetJobRequestedEndDatetime,
      memo: request.origin.memo,
      chargerIds: request.origin.chargerIds,
      chargeOutsideDelivery: request.origin.chargeOutsideDelivery,
      versionId: currentVersionId,
    };

    // request.update.addJobInfos -> deleteJobInfos
    const addedJobs = currentJobInfos.filter(
      (job) =>
        request.origin.jobInfos.findIndex(
          (orgJob) => orgJob.jobId === job.jobId
        ) < 0
    );
    tempUpdate.deleteJobInfos = addedJobs.map((addedJob) => {
      return {
        jobId: addedJob.jobId,
      };
    });

    // request.update.updateJobInfos -> updateJobInfos
    tempUpdate.updateJobInfos = request.update.updateJobInfos?.map(
      (updatedJob) => {
        return {
          jobId: updatedJob.jobId,
          requestedEndDatetimes:
            request.origin.jobInfos.find(
              (orgJob) => orgJob.jobId === updatedJob.jobId
            )?.jobRequestedEndDatetimes ?? [],
        };
      }
    );

    // request.update.deleteJobInfos -> addJobInfos
    const deletedJobInfos = request.origin.jobInfos.filter(
      (orgJob) =>
        currentJobInfos.findIndex((job) => job.jobId === orgJob.jobId) < 0
    );
    tempUpdate.addJobInfos =
      transformSelectTaskJobInfosToInputJobInfos(deletedJobInfos);

    return tempUpdate;
  };

  const result = useCallApi(
    async () => {
      if (request !== undefined) {
        // Deny jobset update
        await requestApprove({
          jobSetId: request.update.jobSetId,
          jobSetApproval: false,
        });

        // Build revert request
        const resetUpdate = await createUndoRequest(request);

        // Retrieve task for version ID
        const deniedJobset = await getMultipleChargeTasks(resetUpdate.jobSetId);
        resetUpdate.versionId = deniedJobset?.versionId ?? "";

        // Revert jobset
        await api.updateTaskMultipleCharge({
          updateTaskDataMultipleCharge: resetUpdate,
        });

        // Wait for jobset status to be notApproved(6)
        await waitTaskStatus(
          resetUpdate.jobSetId,
          [Const.jobsetStatus.notApproved],
          Const.operationTimeout.getTaskCreationProgress
        );

        // Approve jobset revert
        await requestApprove({
          jobSetId: resetUpdate.jobSetId,
          jobSetApproval: true,
        });

        // Get result
        const jobset = await getMultipleChargeTasks(resetUpdate.jobSetId);
        if (jobset === undefined) {
          throw new Error("Registered jobset not found");
        }
        return jobset;
      }
    },
    cancelCallback,
    [
      request,
      requestApprove,
      cancelCallback,
      getMultipleChargeTasks,
      waitTaskStatus,
    ]
  );

  return { ...result, setRevertRequest };
};
